import { useEffect, useState } from 'react';
import { trueFitService } from '@app/lib/truefit/service/truefit-service';
import { trueFitConfig } from '@app/lib/truefit/config';
import { captureException } from '@sentry/nextjs';
import { detectDeviceType } from '@app/lib/truefit/utilities/detect-device-type';
interface RecommendationCTA {
    message: string;
    url: string;
}

interface Recommendation {
    message?: string;
    cta?: RecommendationCTA;
}

interface UseTrueFitReturn {
    loading: boolean;
    error?: Error | null;
    recommendation?: Recommendation | null;
}

const useTrueFit = (
    productID: string,
    locale: string,
    triggerRefetch?: boolean
): UseTrueFitReturn => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<null | Error>(null);
    const [recommendation, setRecommendation] = useState(null);

    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;
        const deviceType = detectDeviceType();

        const endpoint = trueFitConfig.fitRecommendationEndpoint(
            productID,
            locale,
            deviceType
        );

        const initializeTrueFit = async () => {
            try {
                const recommendationResponse =
                    await trueFitService.fetchWithTokenManagement(
                        locale,
                        endpoint,
                        { method: 'GET', signal }
                    );

                const productRecommendation =
                    recommendationResponse.recommendations[productID];
                if (!productRecommendation) {
                    console.warn(
                        'TrueFit: There are no product recommendations for this product'
                    );
                } else {
                    setRecommendation(productRecommendation);
                }
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log('Fetch aborted');
                } else {
                    setError(error ? error : new Error(String(error)));
                    captureException(error);
                }
            } finally {
                setLoading(false);
            }
            // Cleanup function to abort fetch on component unmount
            // This is to avoid memory leaks
            return () => {
                abortController.abort();
            };
        };

        initializeTrueFit();
    }, [productID, locale, triggerRefetch]);

    return { loading, error, recommendation };
};

export default useTrueFit;
